import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD, formatDate, formatClosing, KANBAN_STAGES, stageMeta,
  type AppStage,
} from '../lib/format';
import { pushToast } from '../hooks/useToasts';

interface AppDetail {
  id: string;
  match_id: string;
  partner_profile_id: string;
  grant_program_id: string;
  funding_ask_cad: number;
  required_match_cash: number;
  required_match_inkind: number;
  match_committed_cad: number;
  match_source: string | null;
  stage: AppStage;
  submitted_at: number | null;
  decision_at: number | null;
  awarded_amount_cad: number | null;
  interim_report_due_at: number | null;
  final_report_due_at: number | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
  grant_title: string;
  grant_source_url: string;
  grant_description: string | null;
  funding_min_cad: number | null;
  funding_max_cad: number | null;
  match_required: boolean;
  match_ratio: number | null;
  intake_type: string | null;
  opens_at: number | null;
  grant_closes_at: number | null;
  grant_decision_by: number | null;
  funder_name: string;
  funder_level: string;
  profile_name: string;
  profile_slug: string;
  matching_funds_ceiling: number | null;
}

// Fields that flow through the autosave debounce. Stage is excluded — it's
// changed via the stage selector and saved synchronously so the user gets
// immediate feedback when revealing the Won-only fields.
type EditableField =
  | 'funding_ask_cad'
  | 'required_match_cash'
  | 'required_match_inkind'
  | 'match_committed_cad'
  | 'match_source'
  | 'awarded_amount_cad'
  | 'interim_report_due_at'
  | 'final_report_due_at'
  | 'notes';

type EditableValues = Partial<Record<EditableField, string | number | null>>;

const NUMERIC_FIELDS: ReadonlyArray<EditableField> = [
  'funding_ask_cad', 'required_match_cash', 'required_match_inkind',
  'match_committed_cad', 'awarded_amount_cad',
];
const DATE_FIELDS: ReadonlyArray<EditableField> = [
  'interim_report_due_at', 'final_report_due_at',
];

export default function ApplicationDetailPage() {
  const { id } = useParams({ from: '/applications/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const detailQ = useQuery({
    queryKey: ['application', id],
    queryFn: () => api.get<AppDetail>(`/applications/${id}`),
  });

  if (detailQ.error instanceof ApiError && detailQ.error.status === 401) {
    navigate({ to: '/login' });
    return null;
  }
  if (detailQ.error instanceof ApiError && detailQ.error.status === 404) {
    return (
      <div className="p-6 text-sm">
        <p className="text-neutral-700">Application not found.</p>
        <Link to="/applications" className="text-accent underline">Back to pipeline</Link>
      </div>
    );
  }

  const d = detailQ.data?.data;
  if (!d) return <div className="p-6 text-sm text-neutral-600">Loading application — a moment.</div>;

  return <Editor key={d.id} initial={d} qc={qc} />;
}

function Editor({ initial, qc }: { initial: AppDetail; qc: ReturnType<typeof useQueryClient> }) {
  // Local draft state — autosaves debounce to PATCH /applications/:id.
  const [draft, setDraft] = useState<EditableValues>(() => snapshotFrom(initial));
  const [saveStatus, setSaveStatus] = useState<'idle' | 'pending' | 'saved' | 'error'>('idle');
  const lastSavedRef = useRef<EditableValues>(snapshotFrom(initial));
  const stage = initial.stage;

  const patchMut = useMutation({
    mutationFn: (body: Partial<AppDetail>) =>
      api.patch<AppDetail>(`/applications/${initial.id}`, body),
    onMutate: () => setSaveStatus('pending'),
    onError: (err) => {
      setSaveStatus('error');
      pushToast(err instanceof ApiError ? err.message : 'Save failed', 'error');
    },
    onSuccess: (res) => {
      setSaveStatus('saved');
      lastSavedRef.current = snapshotFrom(res.data);
      qc.setQueryData(['application', initial.id], res);
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['finance-exposure'] });
    },
  });

  // Debounced autosave for editable scalar fields.
  useEffect(() => {
    const diff = diffEditable(draft, lastSavedRef.current);
    if (Object.keys(diff).length === 0) return;
    const t = setTimeout(() => {
      patchMut.mutate(diff as Partial<AppDetail>);
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Stage mutation — synchronous so report fields appear right away on Won.
  const stageMut = useMutation({
    mutationFn: (next: AppStage) => api.patch<AppDetail>(`/applications/${initial.id}`, { stage: next }),
    onSuccess: (res) => {
      qc.setQueryData(['application', initial.id], res);
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['finance-exposure'] });
      pushToast(`Stage: ${stageMeta[res.data.stage].label}`, 'success', 1500);
    },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Stage change failed', 'error'),
  });

  const isWon = stage === 'won';
  const matchTotal = (Number(draft.required_match_cash) || 0) + (Number(draft.required_match_inkind) || 0);
  const ceiling = initial.matching_funds_ceiling;
  const ceilingPct = ceiling != null && ceiling > 0
    ? Math.round(((Number(draft.match_committed_cad) || 0) / ceiling) * 100)
    : null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-5 py-5 space-y-5">
        <header>
          <div className="flex items-center gap-2 text-xs text-neutral-600">
            <Link to="/applications" className="underline">Pipeline</Link>
            <span>/</span>
            <span className="truncate">{initial.profile_name}</span>
          </div>
          <h1 className="mt-1 text-lg font-semibold text-neutral-900 leading-tight">
            {initial.grant_title}
          </h1>
          <p className="mt-1 text-xs text-neutral-600">
            {initial.funder_name}
            <span className="mx-1">·</span>
            <span className="uppercase tracking-wide">{initial.funder_level}</span>
            <span className="mx-1">·</span>
            {formatClosing(initial.grant_closes_at)}
            <span className="mx-1">·</span>
            <a className="underline" href={initial.grant_source_url} target="_blank" rel="noreferrer noopener">
              funder page ↗
            </a>
          </p>
        </header>

        {/* Stage selector — keyboard-equivalent kanban move. */}
        <section aria-labelledby="stage-h">
          <h2 id="stage-h" className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Stage</h2>
          <div role="radiogroup" aria-labelledby="stage-h" className="flex flex-wrap gap-2">
            {KANBAN_STAGES.map((s) => {
              const active = s === stage;
              return (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => { if (!active) stageMut.mutate(s); }}
                  className={
                    'px-3 py-1.5 rounded-md text-sm border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-accent ' +
                    (active
                      ? 'bg-accent text-white border-accent'
                      : 'bg-white border-neutral-300 hover:bg-neutral-50')
                  }
                >
                  {stageMeta[s].label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Matching funds. */}
        <section aria-labelledby="funds-h">
          <div className="flex items-center justify-between">
            <h2 id="funds-h" className="text-xs uppercase tracking-wide text-neutral-600">Matching funds</h2>
            <SaveStatus status={saveStatus} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <NumberField label="Funding ask (CAD)"            field="funding_ask_cad"        draft={draft} setDraft={setDraft} />
            <NumberField label="Match committed (CAD)"        field="match_committed_cad"    draft={draft} setDraft={setDraft} />
            <NumberField label="Required match — cash"        field="required_match_cash"    draft={draft} setDraft={setDraft} />
            <NumberField label="Required match — in kind"     field="required_match_inkind"  draft={draft} setDraft={setDraft} />
          </div>
          <p className="mt-2 text-[11px] text-neutral-600">
            Required match total: <span className="font-medium">{formatCAD(matchTotal)}</span>
            {ceiling != null && (
              <>
                {' '}· Profile ceiling: <span className="font-medium">{formatCAD(ceiling)}</span>
                {ceilingPct != null && (
                  <>
                    {' '}· Committed uses{' '}
                    <span className={ceilingPct >= 100 ? 'font-medium text-rose-700' : ceilingPct >= 70 ? 'font-medium text-amber-700' : 'font-medium text-emerald-700'}>
                      {ceilingPct}%
                    </span>{' '}
                    of ceiling
                  </>
                )}
              </>
            )}
          </p>
          <label className="block mt-3 text-sm">
            <span className="block text-xs text-neutral-700 mb-1">Match source</span>
            <input
              type="text"
              value={(draft.match_source as string | null) ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, match_source: e.target.value }))}
              placeholder="e.g. Reserve transfer, partner cash, in-kind staff time"
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5"
            />
          </label>
        </section>

        {/* Submitted / decision (always visible once relevant). */}
        <section aria-labelledby="timeline-h">
          <h2 id="timeline-h" className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Timeline</h2>
          <table className="w-full text-sm">
            <tbody>
              <Row label="Submitted at"  value={formatDate(initial.submitted_at)} />
              <Row label="Decision at"   value={formatDate(initial.decision_at)} />
              <Row label="Closes (grant)" value={formatDate(initial.grant_closes_at)} />
              <Row label="Decision by (grant)" value={formatDate(initial.grant_decision_by)} />
            </tbody>
          </table>
          <p className="mt-1 text-[11px] text-neutral-500">
            Submitted/decision timestamps auto-fill the first time you move into Submitted, Won, or Lost.
          </p>
        </section>

        {/* Won-only award + reporting block. */}
        {isWon && (
          <section aria-labelledby="award-h" className="border border-emerald-300 bg-emerald-50/50 rounded-md p-3">
            <h2 id="award-h" className="text-xs uppercase tracking-wide text-emerald-900 mb-2">Award & reporting</h2>
            <div className="grid grid-cols-2 gap-3">
              <NumberField label="Awarded amount (CAD)"       field="awarded_amount_cad"       draft={draft} setDraft={setDraft} />
              <DateField   label="Interim report due"          field="interim_report_due_at"    draft={draft} setDraft={setDraft} />
              <DateField   label="Final report due"            field="final_report_due_at"      draft={draft} setDraft={setDraft} />
            </div>
          </section>
        )}

        {/* Notes. */}
        <section aria-labelledby="notes-h">
          <h2 id="notes-h" className="text-xs uppercase tracking-wide text-neutral-600 mb-2">Notes</h2>
          <textarea
            value={(draft.notes as string | null) ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            rows={5}
            placeholder="Working notes — autosaved."
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm leading-snug"
          />
        </section>

        <p className="text-[11px] text-neutral-500">
          App ID <span className="font-mono">{initial.id}</span>{' '}
          · Created {formatDate(initial.created_at)}{' '}
          · Updated {formatDate(initial.updated_at)}
        </p>
      </div>
    </div>
  );
}

// ── Field primitives ─────────────────────────────────────────────────────

function NumberField({
  label, field, draft, setDraft,
}: {
  label: string; field: EditableField;
  draft: EditableValues; setDraft: React.Dispatch<React.SetStateAction<EditableValues>>;
}) {
  const value = draft[field];
  return (
    <label className="block text-sm">
      <span className="block text-xs text-neutral-700 mb-1">{label}</span>
      <input
        type="number"
        min={0}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          const v = e.target.value === '' ? null : Number(e.target.value);
          setDraft((d) => ({ ...d, [field]: v }));
        }}
        className="w-full border border-neutral-300 rounded-md px-2 py-1.5"
      />
    </label>
  );
}

function DateField({
  label, field, draft, setDraft,
}: {
  label: string; field: EditableField;
  draft: EditableValues; setDraft: React.Dispatch<React.SetStateAction<EditableValues>>;
}) {
  const value = draft[field] as number | null | undefined;
  const isoDate = value ? new Date(value).toISOString().slice(0, 10) : '';
  return (
    <label className="block text-sm">
      <span className="block text-xs text-neutral-700 mb-1">{label}</span>
      <input
        type="date"
        value={isoDate}
        onChange={(e) => {
          const v = e.target.value ? Date.parse(`${e.target.value}T23:59:59-05:00`) : null;
          setDraft((d) => ({ ...d, [field]: v }));
        }}
        className="w-full border border-neutral-300 rounded-md px-2 py-1.5"
      />
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-neutral-100 last:border-0">
      <th scope="row" className="text-left font-normal text-neutral-600 py-1.5 pr-3 w-44 align-top">{label}</th>
      <td className="py-1.5">{value}</td>
    </tr>
  );
}

function SaveStatus({ status }: { status: 'idle' | 'pending' | 'saved' | 'error' }) {
  return (
    <span aria-live="polite" className="text-[11px] text-neutral-500">
      {status === 'pending' && 'Saving…'}
      {status === 'saved' && 'Saved'}
      {status === 'error' && <span className="text-rose-700">Save failed</span>}
      {status === 'idle' && ' '}
    </span>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function snapshotFrom(d: AppDetail): EditableValues {
  return {
    funding_ask_cad: d.funding_ask_cad,
    required_match_cash: d.required_match_cash,
    required_match_inkind: d.required_match_inkind,
    match_committed_cad: d.match_committed_cad,
    match_source: d.match_source,
    awarded_amount_cad: d.awarded_amount_cad,
    interim_report_due_at: d.interim_report_due_at,
    final_report_due_at: d.final_report_due_at,
    notes: d.notes,
  };
}

function diffEditable(curr: EditableValues, prev: EditableValues): EditableValues {
  const out: EditableValues = {};
  for (const key of Object.keys(curr) as EditableField[]) {
    const a = curr[key];
    const b = prev[key];
    // Coerce empty-string back to null for nullable text fields.
    const aNorm = (a === '' ? null : a);
    if (!sameScalar(aNorm, b ?? null)) {
      out[key] = numericKey(key) || dateKey(key)
        ? (aNorm === null ? null : Number(aNorm))
        : aNorm;
    }
  }
  return out;
}
function numericKey(k: EditableField) { return NUMERIC_FIELDS.includes(k); }
function dateKey(k: EditableField)    { return DATE_FIELDS.includes(k); }

function sameScalar(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return false;
}
